import LegalLayout from "@/components/LegalLayout";

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" eyebrow="LEGAL" lastUpdated="22 JUNE 2026">
      <p>These Terms of Service ("Terms") govern your use of Hello by Flow (the "Service") operated by Hello by Flow Communications Pvt. Ltd. ("Hello by Flow", "we", "us"). By creating an account or using the Service, you agree to be bound by these Terms.</p>

      <h2>1. The Service</h2>
      <p>Hello by Flow is a cloud-based business calling platform that provides virtual phone numbers, browser-based softphone, call recording, SMS, IVR routing, analytics, and team management. The Service is delivered as Software-as-a-Service via web and desktop browsers.</p>

      <h2>2. Eligibility & Account</h2>
      <ul>
        <li>You must be 18+ years of age and have legal capacity to enter into a contract.</li>
        <li>For business accounts: you must be authorised to bind the entity you represent.</li>
        <li>You must complete KYC verification as required under the Department of Telecommunications (DoT), Government of India and the Telecom Regulatory Authority of India (TRAI) regulations before provisioning any phone number.</li>
        <li>You are responsible for safeguarding your password and for any activity under your account.</li>
      </ul>

      <h2>3. Plans, Fees & Billing</h2>
      <p>The Service is offered under tiered subscription plans (Starter, Growth, Scale) billed per user per month. Fees are exclusive of GST and other applicable taxes. We may revise pricing on 30 days' written notice. Subscriptions auto-renew monthly unless cancelled before the renewal date.</p>

      <h2>4. Acceptable Use</h2>
      <p>You agree not to use the Service for any unlawful purpose including (a) tele-marketing without consent in violation of TRAI's Telecom Commercial Communications Customer Preference Regulations, 2018, (b) fraud, harassment, or impersonation, (c) bulk unsolicited SMS, (d) spreading malware. Detailed rules are in our <a href="/legal/aup">Acceptable Use Policy</a>.</p>

      <h2>5. KYC and Customer Verification</h2>
      <p>As per DoT guidelines for OTT communication services and Customer Acquisition Form (CAF) requirements, we collect and verify proof of identity, address, and (for businesses) registration documents before activating telecom services. You warrant that all documents submitted are genuine. Forged documents will result in immediate suspension and may be reported to the relevant authorities.</p>

      <h2>6. Data Protection</h2>
      <p>We process Personal Data in accordance with the Digital Personal Data Protection Act, 2023 (India), the EU General Data Protection Regulation where applicable, and our <a href="/legal/privacy">Privacy Policy</a>. Call recordings and SMS content are encrypted in transit and at rest.</p>

      <h2>7. Intellectual Property</h2>
      <p>The Service, its codebase, designs, and trademarks are owned by Hello by Flow. You retain ownership of your customer data, recordings, and contacts. By using the Service you grant us a limited licence to process this data to provide the Service.</p>

      <h2>8. Service Availability</h2>
      <p>We target 99.99% monthly uptime. Service Credits for downtime are described in our SLA, attached to enterprise contracts. We are not liable for outages caused by upstream carriers, force majeure, or your network.</p>

      <h2>9. Termination</h2>
      <p>You may cancel anytime from Settings → Billing. We may suspend or terminate accounts that violate these Terms, are involved in fraud, or repeatedly fail KYC. Upon termination, your data is retained for 30 days before secure deletion (longer if required by law).</p>

      <h2>10. Liability</h2>
      <p>To the extent permitted by law, Hello by Flow's aggregate liability under these Terms is limited to fees paid by you in the 12 months preceding the claim. We are not liable for indirect, incidental, or consequential damages.</p>

      <h2>11. Governing Law & Jurisdiction</h2>
      <p>These Terms are governed by the laws of India. Disputes will be subject to the exclusive jurisdiction of the courts at Bengaluru, Karnataka. Arbitration may be invoked under the Arbitration & Conciliation Act, 1996.</p>

      <h2>12. Changes</h2>
      <p>We may update these Terms by posting a revised version with a new "Last Updated" date. Material changes will be notified 30 days in advance via email.</p>

      <h2>13. Contact</h2>
      <p>Questions about these Terms: <strong>legal@hellobyflow.com</strong>. Postal: Hello by Flow Communications Pvt. Ltd., Bengaluru, Karnataka, India.</p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" eyebrow="LEGAL" lastUpdated="22 JUNE 2026">
      <p>This Privacy Policy explains how Hello by Flow Communications Pvt. Ltd. ("Hello by Flow") collects, uses, stores, and shares your personal data when you use our Service. We are committed to compliance with the <strong>Digital Personal Data Protection Act, 2023 (DPDPA)</strong> in India and the <strong>EU General Data Protection Regulation (GDPR)</strong> where applicable.</p>

      <h2>1. Data we collect</h2>
      <h3>Account data</h3>
      <ul>
        <li>Name, work email, password (hashed with bcrypt), workspace name, role.</li>
      </ul>
      <h3>KYC documents (required by DoT/TRAI)</h3>
      <ul>
        <li><strong>Business accounts:</strong> Certificate of Incorporation, GST/PAN, authorised signatory ID.</li>
        <li><strong>Individual accounts:</strong> Aadhaar, Passport, Driving Licence, or Voter ID (front + back), address proof, optional selfie.</li>
      </ul>
      <h3>Service data</h3>
      <ul>
        <li>Call metadata (caller, callee, duration, direction, status), call recordings (only when explicitly enabled by the agent), SMS content, contacts you import, IVR flow definitions, analytics events.</li>
      </ul>
      <h3>Device & log data</h3>
      <ul>
        <li>IP address, browser user-agent, timestamps, error logs.</li>
      </ul>

      <h2>2. Purposes of processing (DPDPA Sec. 4)</h2>
      <ul>
        <li>To provide the Service you signed up for (lawful basis: contract performance).</li>
        <li>To comply with DoT/TRAI telecom regulations, anti-fraud screening, and CAF retention.</li>
        <li>To bill you and prevent payment fraud.</li>
        <li>To improve product quality (aggregated, de-identified analytics only).</li>
      </ul>

      <h2>3. Data Principal rights (DPDPA Sec. 11–14)</h2>
      <p>You may at any time:</p>
      <ul>
        <li><strong>Access</strong> your personal data (Settings → Profile or write to dpo@hellobyflow.com).</li>
        <li><strong>Correct</strong> inaccurate data.</li>
        <li><strong>Erase</strong> data (subject to legal retention obligations under the Information Technology Act, 2000 and CAF rules — call detail records retained for 12 months).</li>
        <li><strong>Withdraw consent</strong> — your account is deactivated within 7 days.</li>
        <li><strong>Lodge a grievance</strong> with our DPO (see Section 8).</li>
      </ul>

      <h2>4. Sharing</h2>
      <p>We share data only with:</p>
      <ul>
        <li><strong>Sub-processors:</strong> MongoDB Atlas (database hosting, India region), AWS (storage), Twilio (carrier connectivity).</li>
        <li><strong>Law enforcement:</strong> on receipt of a lawful order under the IT Act or CrPC. We publish a transparency report annually.</li>
        <li><strong>Never with advertisers.</strong> We do not sell your data.</li>
      </ul>

      <h2>5. International transfers</h2>
      <p>Personal data of Indian Data Principals is stored on infrastructure located within India. Limited metadata may be processed in approved jurisdictions (Singapore, EU) under standard contractual clauses.</p>

      <h2>6. Retention</h2>
      <ul>
        <li>Account data: while your account is active + 30 days post-termination.</li>
        <li>KYC documents: 5 years post-termination (DoT requirement).</li>
        <li>Call Detail Records (CDR): 12 months (DoT requirement).</li>
        <li>Call recordings: as long as you keep them, deleted within 7 days of account termination.</li>
      </ul>

      <h2>7. Security</h2>
      <p>Encryption in transit (TLS 1.3), at rest (AES-256). Annual penetration testing. SOC 2 Type II audited. Bcrypt password hashing. Brute-force lockouts. Role-based access control.</p>

      <h2>8. Grievance Officer (DPDPA Sec. 13)</h2>
      <p><strong>Data Protection Officer:</strong> dpo@hellobyflow.com<br/>
      Hello by Flow Communications Pvt. Ltd., Bengaluru, Karnataka, India.<br/>
      We respond to all grievances within 30 days.</p>

      <h2>9. Children</h2>
      <p>The Service is not intended for users under 18. We do not knowingly collect data from minors.</p>

      <h2>10. Updates</h2>
      <p>Material changes will be notified via email and in-app banner 30 days in advance.</p>
    </LegalLayout>
  );
}

export function AcceptableUse() {
  return (
    <LegalLayout title="Acceptable Use Policy" eyebrow="LEGAL" lastUpdated="22 JUNE 2026">
      <p>This Acceptable Use Policy ("AUP") sets out rules that apply to all use of the Hello by Flow Service. Violations may result in suspension, termination, and reporting to authorities including DoT, TRAI, Cyber Crime Police, and CERT-In.</p>

      <h2>Prohibited content & activity</h2>
      <ul>
        <li>Tele-marketing or SMS marketing without explicit recipient consent (violates TRAI TCCCPR 2018).</li>
        <li>Calls or SMS to numbers on the National Do-Not-Call (DND) registry without scrubbing.</li>
        <li>Fraud, phishing, KYC impersonation, or any scheme intended to deceive recipients.</li>
        <li>Harassment, threats, hate speech, or stalking.</li>
        <li>Distribution of child sexual abuse material — we have zero tolerance and will report to NCRB/NCMEC.</li>
        <li>Distribution of malware, ransomware, or links to phishing pages.</li>
        <li>Caller ID spoofing or manipulation that violates Section 25 of the Indian Telegraph Act.</li>
        <li>Bulk auto-dialling without recipient consent (Section 11, TCCCPR 2018).</li>
        <li>Premium-rate fraud, traffic pumping, or any artificial inflation of call traffic.</li>
        <li>Voice-bot calls without disclosing AI/bot identity at the start of the call.</li>
      </ul>

      <h2>Calling-hour restrictions</h2>
      <p>Promotional voice & SMS communications are permitted between 09:00 and 21:00 IST only, per TRAI directives. Transactional communications (OTPs, booking confirmations) are exempt.</p>

      <h2>Recording & consent</h2>
      <p>You must obtain consent from the other party before recording a call. Hello by Flow can be configured to play an opening "this call is being recorded" beep — turn it on in Settings → Compliance.</p>

      <h2>KYC of your customers</h2>
      <p>If you resell Hello by Flow numbers to your own customers (Scale plan), you must perform KYC on them per DoT subscriber verification rules and produce records on demand.</p>

      <h2>Enforcement</h2>
      <p>Violations are detected by abuse heuristics, recipient complaints, carrier reports, and government notices. We may immediately suspend without notice for severe violations. Repeat violators forfeit subscription fees.</p>

      <p>Report abuse: <strong>abuse@hellobyflow.com</strong></p>
    </LegalLayout>
  );
}

export function Compliance() {
  return (
    <LegalLayout title="Telecom Compliance & KYC" eyebrow="LEGAL" lastUpdated="22 JUNE 2026">
      <p>Hello by Flow operates within India's telecom regulatory framework. This page summarises the compliance regime that applies to the Service and to you as a subscriber.</p>

      <h2>Regulators we comply with</h2>
      <ul>
        <li><strong>Department of Telecommunications (DoT), Ministry of Communications, Government of India</strong> — licensing, lawful interception, subscriber verification.</li>
        <li><strong>Telecom Regulatory Authority of India (TRAI)</strong> — quality of service, consumer protection, anti-spam (TCCCPR 2018).</li>
        <li><strong>Indian Computer Emergency Response Team (CERT-In)</strong> — incident reporting under CERT-In Directions 2022.</li>
        <li><strong>Ministry of Electronics & IT (MeitY)</strong> — IT Act 2000, intermediary guidelines, DPDPA 2023.</li>
      </ul>

      <h2>KYC — what we collect and why</h2>
      <p>Under DoT's <strong>Subscriber Verification Guidelines</strong> and Customer Acquisition Form (CAF) requirements, every phone number issued in India must be tied to a verified subscriber.</p>

      <h3>Business subscribers</h3>
      <ul>
        <li>Certificate of Incorporation (MCA) or partnership deed.</li>
        <li>GSTIN or PAN of the entity.</li>
        <li>Government-issued ID of the Authorised Signatory.</li>
        <li>Board resolution / authorisation letter (for accounts with more than 10 lines).</li>
        <li>Registered address proof.</li>
      </ul>

      <h3>Individual subscribers</h3>
      <ul>
        <li>One of: Aadhaar (offline-XML preferred), Passport, Driving Licence, Voter ID, NREGA Job Card.</li>
        <li>For Aadhaar — only the masked variant (last 4 digits visible) is retained.</li>
        <li>Optional live selfie for face match (recommended).</li>
        <li>Current address proof if different from ID.</li>
      </ul>

      <h2>Verification process</h2>
      <p>Documents are checked for: (a) tamper detection, (b) cross-validation with issuing authority APIs where available (UIDAI, MCA, NSDL), (c) sanctions/PEP screening, (d) face match against the selfie. Approved within seconds for clean submissions; flagged ones go through manual review.</p>

      <h2>Lawful interception & disclosure</h2>
      <p>Under Section 5(2) of the Indian Telegraph Act, 1885 and the IT Act, 2000, we are legally required to provide call detail records and content to authorised Law Enforcement Agencies on receipt of a properly issued order. We do not provide bulk access. Every request is logged and reviewed by our DPO.</p>

      <h2>Data localisation</h2>
      <p>Subscriber data and CDRs for Indian numbers are stored on infrastructure located within India per the National Telecom Policy. Cross-border processing is restricted to anonymised metadata.</p>

      <h2>CERT-In incident reporting</h2>
      <p>We report cyber security incidents to CERT-In within 6 hours per the 28 April 2022 directions.</p>

      <h2>Numbering plan</h2>
      <p>Hello by Flow numbers in India are allocated from the DoT-approved numbering blocks. Mobile (10-digit), landline (with city code), and 1800-toll-free series are supported.</p>

      <h2>Anti-spam (TCCCPR 2018)</h2>
      <p>All promotional traffic is templatised, header-registered, and consent-scrubbed via the DLT (Distributed Ledger Technology) registries operated by access providers. Unregistered promotional traffic is blocked at the network edge.</p>

      <h2>Contact</h2>
      <p>Compliance Officer: <strong>compliance@hellobyflow.com</strong><br/>
      Nodal Officer (24×7): <strong>nodal@hellobyflow.com</strong></p>
    </LegalLayout>
  );
}

export function Cookies() {
  return (
    <LegalLayout title="Cookie Policy" eyebrow="LEGAL" lastUpdated="22 JUNE 2026">
      <p>This Cookie Policy explains how Hello by Flow uses cookies and similar tracking technologies.</p>

      <h2>What we use</h2>
      <h3>Strictly necessary (always on)</h3>
      <ul>
        <li><strong>access_token</strong> — your authentication cookie (HttpOnly + Secure + SameSite=Lax). Set by our backend; not accessible to JavaScript. Required to keep you signed in.</li>
        <li><strong>refresh_token</strong> — HttpOnly cookie used to rotate the access token every 15 minutes; 90-day lifetime.</li>
        <li><strong>csrf_token</strong> — JS-readable cookie used by our frontend to mirror as <code>X-CSRF-Token</code> on state-changing requests.</li>
        <li><strong>hbf_support_session_v1</strong> — anonymous session ID for the Hello by Flow support chatbot. Stored in localStorage.</li>
      </ul>
      <h3>Functional</h3>
      <ul>
        <li>UI preferences (dialer position, sidebar collapsed state).</li>
      </ul>
      <h3>Analytics</h3>
      <p>We use first-party, IP-anonymised analytics to count page views and identify performance issues. We do <strong>not</strong> use Google Analytics or any third-party advertising tracker.</p>

      <h2>Your choices</h2>
      <p>You can clear cookies in your browser settings at any time. Clearing strictly necessary cookies will log you out.</p>

      <h2>Do Not Track</h2>
      <p>We honour browser-level Do Not Track signals for the analytics bucket.</p>

      <h2>Contact</h2>
      <p>Questions: <strong>privacy@hellobyflow.com</strong></p>
    </LegalLayout>
  );
}
